/**
 * CreatorProtectedRoute - Auth wrapper for Creator Portal
 * 
 * Handles:
 * 1. User authentication via Supabase
 * 2. Email-based admin bootstrap (links user_id + sets status='active' for admins)
 * 3. Status upgrade from 'invited' to 'active' on first portal access
 * 4. Block disabled accounts with clear message
 * 5. Redirect to /creator/auth if not authenticated
 */

import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AccountDisabledScreen } from "./AccountDisabledScreen";
import { useAuth } from "@/contexts/AuthContext";

interface CreatorProtectedRouteProps {
  children: React.ReactNode;
}

type AccountStatus = 'loading' | 'authenticated' | 'disabled' | 'not-linked' | 'unauthenticated';

const CreatorProtectedRoute = ({ children }: CreatorProtectedRouteProps) => {
  // FIX-R33-LOW-3: pull signOut from AuthContext for consistent
  // localStorage cleanup.
  //
  // ARC-A4: also pull `user` and `isLoading` from the same canonical
  // AuthContext. Pre-A4 this route owned its own
  // `supabase.auth.onAuthStateChange` listener + `getSession()` call
  // that duplicated AuthContext's canonical listener; on every auth
  // event AuthContext's listener AND this route's listener both fired,
  // each running their own setState path. Post-A4 the user state comes
  // from useAuth() and we drop the per-route listener entirely. The
  // existing `checkCreatorAccount` effect already keys on `user` and
  // re-runs whenever the context user changes, so the creator-account
  // bootstrap / status-upgrade / admin-binding semantics are
  // preserved verbatim.
  const { signOut: contextSignOut, user, isLoading: authLoading } = useAuth();
  const [accountStatus, setAccountStatus] = useState<AccountStatus>('loading');
  const [isAdmin, setIsAdmin] = useState(false);

  // ARC-A4: replaces the dropped `supabase.auth.onAuthStateChange`
  // listener. Wait for AuthContext to finish its initial session check
  // (`isLoading: false`) before deciding 'unauthenticated' — otherwise
  // the brief mount-time window where context.user is still `null`
  // would falsely flip the route into the unauthenticated redirect.
  // When AuthContext resolves to `user === null`, status flips to
  // 'unauthenticated' which fires the same `<Navigate to="/creator/auth">`
  // redirect as before. The downstream `checkCreatorAccount` effect
  // continues to gate on `user` (early-returns when null), so the
  // 'loading' → 'authenticated' / 'disabled' / 'not-linked'
  // transitions are unchanged.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setAccountStatus('unauthenticated');
    }
  }, [user, authLoading]);

  // Check for creator account when user is authenticated
  useEffect(() => {
    if (!user) return;

    const checkCreatorAccount = async () => {
      try {
        // First, check if user already has a linked account
        const { data: linkedAccount, error } = await supabase
          .from('training_creator_accounts')
          .select('id, status, is_admin')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking creator account:', error);
          setAccountStatus('not-linked');
          return;
        }

        if (linkedAccount) {
          // Account exists and is linked
          if (linkedAccount.status === 'disabled') {
            setAccountStatus('disabled');
            return;
          }

          if (linkedAccount.status === 'invited') {
            // First portal access - upgrade to active
            console.log('Upgrading creator status from invited to active');
            const { error: updateError } = await supabase
              .from('training_creator_accounts')
              .update({ 
                status: 'active',
                last_activity_at: new Date().toISOString()
              })
              .eq('id', linkedAccount.id);

            if (updateError) {
              console.error('Error upgrading status:', updateError);
            }
          } else {
            // Update last activity
            await supabase
              .from('training_creator_accounts')
              .update({ last_activity_at: new Date().toISOString() })
              .eq('id', linkedAccount.id);
          }

          setIsAdmin(linkedAccount.is_admin);
          setAccountStatus('authenticated');
          return;
        }

        // No linked account - try email-based bootstrap
        if (user.email) {
          const { data: unlinkedAccount, error: lookupError } = await supabase
            .from('training_creator_accounts')
            .select('id, is_admin')
            .eq('email', user.email)
            .is('user_id', null)
            .maybeSingle();

          if (lookupError) {
            console.error('Error looking up by email:', lookupError);
          }

          if (unlinkedAccount) {
            console.log('Found unlinked account by email, bootstrapping...', { 
              is_admin: unlinkedAccount.is_admin 
            });
            
            // CORRECTED: Admin gets status='active' immediately
            const newStatus = unlinkedAccount.is_admin ? 'active' : 'active';
            
            const { error: updateError } = await supabase
              .from('training_creator_accounts')
              .update({ 
                user_id: user.id,
                status: newStatus,
                last_activity_at: new Date().toISOString()
              })
              .eq('id', unlinkedAccount.id);

            if (updateError) {
              console.error('Error linking account:', updateError);
              setAccountStatus('not-linked');
              return;
            }

            // Also bind internal_admins if matching email with null user_id
            if (user.email) {
              const { error: internalAdminError } = await supabase
                .from('internal_admins')
                .update({ user_id: user.id })
                .eq('email', user.email)
                .is('user_id', null);

              if (internalAdminError && internalAdminError.code !== 'PGRST116') {
                console.log('[CreatorProtectedRoute] Internal admin binding attempted:', internalAdminError.message);
              }
            }

            setIsAdmin(unlinkedAccount.is_admin);
            setAccountStatus('authenticated');
            return;
          }
        }

        // No matching account found
        setAccountStatus('not-linked');
      } catch (err) {
        console.error('Unexpected error checking creator account:', err);
        setAccountStatus('not-linked');
      }
    };

    checkCreatorAccount();
  }, [user]);

  if (accountStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated - redirect to creator auth
  if (accountStatus === 'unauthenticated') {
    return <Navigate to="/creator/auth" replace />;
  }

  // Account disabled - show disabled screen
  if (accountStatus === 'disabled') {
    return <AccountDisabledScreen />;
  }

  // Authenticated but no creator account
  if (accountStatus === 'not-linked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Account Not Linked</CardTitle>
            <CardDescription>
              Your account is not linked to any training content yet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              If you're a training content creator, please contact the OptiBay team to have your 
              account linked to your training artifacts.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={async () => {
                  await contextSignOut();
                  window.location.href = '/creator/auth';
                }}
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default CreatorProtectedRoute;
